/**
 * Access administration in the Control Center: user accounts and the
 * role/capability registry that governs what each account may do.
 *
 * Split from `controlCenter` because access administration is a surface of
 * its own: Users and Roles share their vocabulary — assignments,
 * capabilities, scopes — with each other and with nothing else there.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const controlCenterAccess = {
  'workplaces.title': 'Workplaces',
  'workplaces.classificationSettings': 'Workplace classification profile',
  'workplaces.profile': 'National profile',
  'workplaces.changeProfile': 'Change classification profile',
  'workplaces.classificationUnsaved': 'Organization workplace classification profile',
  'workplaces.empty': 'No workplaces.',
  'workplaces.loadFailed': 'Workplaces could not be loaded.',
  'workplaces.add': 'Add workplace',
  'workplaces.remove': 'Remove workplace',
  'workplaces.defaultBranch': 'New organization default branch',
  'workplaces.edit': 'Edit workplace',
  'workplaces.save': 'Save workplace',
  'workplaces.branch': 'Branch',
  'workplaces.profession': 'Profession',
  'workplaces.position': 'Position',
  'workplaces.specialty': 'Specialty',
  'workplaces.active': 'Workplace is active',
  'workplaces.available': 'Available',
  'workplaces.unavailable': 'Unavailable',
  'workplaces.from': 'Valid from',
  'workplaces.until': 'Valid until',
  'workplaces.il': 'Israel Ministry of Health',
  'workplaces.ru': 'Russian Ministry of Health',
  'workplaces.convert': 'Use the organization profile and select new values',
  'workplaces.unsaved': 'Workplace details',
  'workplaces.failed': 'Workplaces are unavailable. Your entries are kept.',
  'workplaces.changed': 'This workplace changed. Reload its stored revision before saving.',
  'workplaces.profileChanged': 'The organization classification profile changed. Reload before saving.',
  'workplaces.duplicate': 'An equivalent workplace already covers this period.',
  'workplaces.futureBookings': 'Resolve the affected future appointments before changing this workplace.',
  'workplaces.affectedBookings': 'Affected appointments',
  'workplaces.bookingCount': 'Affected appointments: {count}',
  'workplaces.bookingLink': 'Open appointment {number} in a new tab',
  'workplaces.codingInvalid': 'A selected code does not belong to this workplace field.',
  'workplaces.branchUnavailable': 'This branch is unavailable for an active workplace.',
  'workplaces.invalid': 'Check the workplace fields and validity period.',
  'workplaces.forbidden': 'You may not make this workplace change.',
  'workplaces.one': 'Workplace',
  'workplaces.subtitle': 'Which branch each clinician works at, and under what classification',
  'workplaces.employee': 'Employee',
  'workplaces.state': 'State',
  'workplaces.closed': 'Ended',
  'workplaces.create': 'New workplace',
  'workplaces.saved': 'Workplace saved',
  'workplaces.loading': 'Loading workplaces',
  'workplaces.reloadFailed': 'Could not load the saved revision.',
  'workplaces.readOnly': 'This workplace is read-only for you.',
  'workplaces.open': 'Open Workplaces',
  'workplaces.current': 'Current',
  'workplaces.remembered': 'Last time',
  'workplaces.rememberFailed': 'The workplace choice could not be saved. Try again.',
  'workplaces.enterWithout': 'Enter without a workplace',
  'workplaces.editClassification': 'Change classification',
  'organizationSetup.title': 'Organization setup',
  'organizationSetup.create': 'Create organization',
  'organizationSetup.empty': 'No organization setups.',
  'organizationSetup.failed': 'Organization setup is unavailable. Your entries are kept.',
  'organizationSetup.changed': 'This setup changed. Refresh its stored state before continuing.',
  'organizationSetup.inputConflict': 'This request was already saved with different details. Close the form and open the stored setup.',
  'organizationSetup.closed': 'This setup no longer accepts this action.',
  'organizationSetup.forbidden': 'You do not have permission to manage organization setup.',
  'organizationSetup.ownerEmail': 'Owner email',
  'organizationSetup.unsaved': 'New organization details',
  'organizationSetup.unsavedLink': 'Owner invitation link not yet copied',
  'organizationSetup.discard': 'Discard organization details?',
  'organizationSetup.discardLink': 'The personal invitation link will no longer be displayed. Close anyway?',
  'organizationSetup.cancel': 'Cancel setup',
  'organizationSetup.cancelConfirm': 'Cancel this setup and revoke its owner invitation?',
  'organizationSetup.refresh': 'Refresh setup status',
  'organizationSetup.initialize': 'Continue setup',
  'organizationSetup.initializing': 'Preparing...',
  'organizationSetup.busy': 'Setup is already running. Refresh its status before retrying.',
  'organizationSetup.refreshBeforeRetry': 'Refresh the saved status before another attempt.',
  'organizationSetup.protocolsFailed': 'Protocols could not be installed. Completed steps are kept.',
  'organizationSetup.printFormsFailed': 'Print forms could not be installed. Completed steps are kept.',
  'organizationSetup.langfuseFailed': 'The prompt and telemetry project could not be verified.',
  'organizationSetup.admissionFailed': 'The final organization configuration could not be completed. Completed steps are kept.',
  'organizationSetup.awaitingLangfuse': 'Awaiting Langfuse project registration by the setup agent.',
  'organizationSetup.reloadFailed': 'Could not refresh this setup. The displayed state is unchanged.',
  'organizationSetup.protocols': 'Protocols',
  'organizationSetup.printForms': 'Print forms',
  'organizationSetup.langfuse': 'Prompts and telemetry',
  'organizationSetup.ownerAccepted': 'Ownership accepted. The organization is being prepared and is not yet available for clinical work.',
  'organizationSetup.state.awaiting_owner': 'Awaiting owner',
  'organizationSetup.state.initializing': 'Preparing organization',
  'organizationSetup.state.failed': 'Setup needs attention',
  'organizationSetup.state.ready': 'Ready',
  'organizationSetup.state.cancelled': 'Cancelled',
  'accountEmail.title': 'Verified account email',
  'accountEmail.address': 'Email address',
  'accountEmail.add': 'Add email',
  'accountEmail.request': 'Send code',
  'accountEmail.code': 'Verification code',
  'accountEmail.confirm': 'Verify email',
  'accountEmail.resend': 'Send a new code',
  'accountEmail.refresh': 'Refresh email status',
  'accountEmail.submitted': 'Verification email submitted.',
  'accountEmail.uncertain': 'Email submission was not confirmed. Check your mailbox before requesting another code.',
  'accountEmail.verified': 'Email verified.',
  'accountEmail.failed': 'Email verification is unavailable. Your entries have been kept.',
  'accountEmail.cooldown': 'Wait one minute before requesting another code.',
  'accountEmail.missing': 'This challenge is no longer current. Refresh the status or request a new code.',
  'accountEmail.expired': 'The code has expired or has no attempts left. Request a new code.',
  'accountEmail.invalid': 'The verification code is incorrect.',
  'accountEmail.occupied': 'This verified email belongs to another account. Sign in to that account; accounts are not merged.',
  'accountEmail.throttled': 'Too many requests. Try again in fifteen minutes.',
  'accountEmail.unsaved': 'Account email verification',
  'users.invites.title': 'Invitations',
  'users.invites.create': 'Invite user',
  'users.invites.recipient': 'Recipient email',
  'users.invites.channel': 'Invitation channel',
  'users.invites.email': 'Email',
  'users.invites.telegram': 'Telegram',
  'users.invites.telegramRecipient': 'Telegram recipient',
  'users.invites.web': 'Link',
  'users.invites.webRecipient': 'Link recipient',
  'users.invites.kind.employee': 'Employee',
  'users.invites.kind.developer': 'Developer',
  'users.invites.kind.first_owner': 'First owner',
  'users.invites.kind.owner': 'Owner',
  'users.invites.state.pending': 'Awaiting acceptance',
  'users.invites.state.expired': 'Expired',
  'users.invites.state.accepted': 'Accepted',
  'users.invites.state.revoked': 'Revoked',
  'users.invites.delivery': 'Submission',
  'users.invites.delivery.pending': 'Not submitted',
  'users.invites.delivery.submitted': 'Submitted to mail server',
  'users.invites.delivery.uncertain': 'Submission outcome unknown',
  'users.invites.delivery.refused': 'Submission refused',
  'users.invites.personalLink': 'Personal Telegram link',
  'users.invites.webLink': 'Invitation link',
  'users.invites.expires': 'Expires',
  'users.invites.empty': 'No invitations',
  'users.invites.resend': 'Issue a new link',
  'users.invites.revoke': 'Revoke invitation',
  'users.invites.resendConfirm': 'The previous link will stop working. The new invitation will be valid for three days.',
  'users.invites.revokeConfirm': 'This invitation will no longer grant membership. Existing memberships will not change.',
  'users.invites.copy': 'Copy invitation link',
  'users.invites.copied': 'Link copied',
  'users.invites.copyNamed': 'Copy the invitation link: {name}, valid until {expires}',
  'users.invites.copyFailed': 'Could not copy the link.',
  'users.invites.linkUnavailable': 'The link cannot be copied. Issue a new link; the previous one will stop working.',
  'users.invites.unsaved': 'An unsent user invitation is open.',
  'users.invites.changed': 'This invitation changed. Check its current status before another action.',
  'users.invites.branchInvalid': 'The selected branches are no longer available. Check the organization branches.',
  'users.invites.notReady': 'Organization setup is not complete.',
  'users.invites.telegramUnavailable': 'The staff Telegram bot is unavailable. The invitation may have been saved without a share link.',
  'users.invites.throttled': 'Too many invitation attempts. Try again later.',
  'users.invites.invalid': 'Check the recipient, roles and workplaces.',
  'users.invitation.title': "Staff invitation",
  'users.invitation.missing': "This invitation link is invalid. Ask the organization for a new link.",
  'users.invitation.unavailable': "The invitation service is unavailable. The last action may not have completed; check the invitation before trying again.",
  'users.invitation.identityConflict': "This email or Telegram identity belongs to another account. Sign in to that account.",
  'users.invitation.signInRequired': "Sign in to the account associated with this invitation.",
  'users.invitation.accountConflict': "Sign out before creating a different account.",
  'users.invitation.accountKind': "This invitation is not available for this account type.",
  'users.invitation.alreadyMember': "This account already has access to the organization.",
  'users.invitation.restoreRequired': "This membership is disabled. Confirm restoration to accept the new invitation.",
  'users.invitation.closed': "This invitation is no longer open.",
  'users.invitation.changed': "The invitation or its organization settings changed. Ask the organization for a new invitation.",
  'users.invitation.credentialsRequired': "Choose a username and a password that meets the password policy.",
  'users.invitation.invalidInput': "Check the entered account and name details.",
  'users.invitation.telegramRequired': "Reopen this invitation from the staff Telegram bot to confirm your identity.",
  'users.invitation.telegramUnknown': "This Telegram identity has no account yet. Create an account or sign in to your existing account.",
  'users.invitation.passwordRequired': "Change your temporary password before accepting the invitation.",
  'users.invitation.forbidden': "You cannot accept this invitation with the current account or application access.",
  'users.invitation.expired': "This invitation has expired. Ask the organization for a new link.",
  'users.invitation.revoked': "This invitation was revoked by the organization.",
  'users.invitation.joined': "Membership confirmed.",
  'users.invitation.disabled': "This invitation was already accepted, but the membership is now disabled.",
  'users.invitation.accepted': "This invitation was already accepted. Sign in to the accepting account to check its current access.",
  'users.invitation.account': "Account",
  'users.invitation.welcome': "Welcome. Create your account to join; everything else is set in your own card afterwards.",
  'users.invitation.useExisting': "I already have an account",
  'users.invitation.createInstead': "Create a new account",
  'users.invitation.addPassword': "Add a username and password",
  'users.invitation.restore': "Restore my membership in this organization",
  'users.invitation.working': "Please wait...",
  'users.invitation.accept': "Confirm membership",
  'users.invitation.create': "Create account and join",
  'users.invitation.telegramSignIn': "Sign in with Telegram",
  'users.invitation.recheck': "Check invitation status",
  'users.invitation.unsaved': "The invitation form has unsaved account or profile details.",
  "users.title": "Staff",
  'users.subtitle': 'Organization employees, roles and membership.',
  'users.loading': 'Loading users',
  'users.loadFailed': 'Could not load users',
  'users.forbidden': 'You may not read users for this clinic',
  'users.tableLabel': 'Clinic users',
  'users.empty': 'No visible users',
  'users.emptyDetail': 'User accounts created for this clinic will appear here.',
  'users.noMatches': 'No users match these filters',
  'users.noMatchesDetail': 'Widen or clear the column filters to return to the full user list.',
  'users.column.name': 'Name',
  'users.column.username': 'Username',
  'users.column.roles': 'Roles',
  'users.column.status': 'Status',
  'users.column.kind': 'Kind',
  'users.column.contact': 'Contact',
  'users.status.active': 'Active',
  'users.status.disabled': 'Blocked',
  'users.badge.owner': 'Owner',
  'users.badge.you': 'You',
  'users.badge.admin': 'Administrator',
  'users.actions': 'Actions',
  'users.editNamed': 'Edit {name}',
  'users.sections': 'User sections',
  'users.createTitle': 'Add user',
  'users.username': 'Username',
  'users.initialPassword': 'Initial password',
  'users.initialPasswordHint': 'The new account can be required to change it after login.',
  'users.lifecycleStatus': 'Login status',
  'users.passwordChangeRequired': 'Require password change at next login',
  'users.roles': 'Roles',
  'users.transferOwnership': 'Transfer ownership',
  'users.transferTitle': 'Transfer clinic ownership',
  'users.transferDescription': 'Make {name} the clinic owner. Your regular roles will not change.',
  'users.transferCancel': 'Cancel',
  'users.membershipStatus': 'Organization access',
  'users.remove': 'Delete',
  'users.removeNamed': 'Delete {name}',
  'users.removeConfirm': 'They will disappear from the user list of this organization. Only an employee with no medical records or objects here can be deleted; otherwise they can only be blocked. Authored objects and access to other organizations are preserved. If no other organization remains, the global account will be blocked.',
  'users.removeFailed': 'The user could not be deleted.',
  'users.removeMedical': 'This user cannot be deleted: they have medical records or objects in this organization. Block them instead.',
  'users.removed': 'The user was removed from this organization. Their account in other organizations is unchanged.',
  'users.removedAccountBlocked': 'The user was removed from this organization. No other organization remains, so their global account was blocked.',
  'users.membershipDisable': 'Block',
  'users.membershipRestore': 'Unblock',
  'users.membershipDisableNamed': 'Block {name}',
  'users.membershipRestoreNamed': 'Unblock {name}',
  'users.membershipDisableConfirm': 'They will not be able to work in this organization until unblocked. Roles, history and access to other organizations stay unchanged.',
  'users.membershipRestoreConfirm': 'They will regain access to this organization with their existing roles.',
  'users.membershipUnsaved': 'Save or cancel pending edits before changing access.',
  'users.accessHint': 'A blocked employee cannot work here until unblocked. Only an employee with no medical records here can be deleted.',
  'users.transferConfirm': 'Transfer ownership',
  'users.transferring': 'Transferring',
  'users.transferFailed': 'Could not transfer clinic ownership.',
  'users.practitioner': 'Practitioner profile',
  'users.practitionerHint': 'Use this only for clinicians who need practitioner context in clinical workflows.',
  'users.profession': 'MoH profession',
  'users.expertise': 'MoH expertise',
  'users.codingNone': 'Not specified',
  'users.create': 'Create user',
  'users.creating': 'Creating',
  'users.created': 'User created',
  'users.saving': 'Saving',
  'users.changedConcurrently': 'Someone else changed this user while the form was open.',
  'users.usernameTaken': 'That username is already in use.',
  'users.createFailed': 'Could not create the user.',
  'users.unsavedIntent': 'User form \u2014 entries that were never saved',
  'users.saveForbidden': 'You may not change users.',
  'users.saveFailed': 'Could not save the user.',
  'users.detailsReadOnly': 'This employee\u2019s contacts cannot be changed here.',
  'users.rolesOwnerNote': 'Changing regular roles does not transfer ownership or remove owner authority.',
  'users.rolesNone': 'No assignable roles',
  'users.saveRoles': 'Save roles',
  'users.savingRoles': 'Saving roles',
  'users.rolesSaved': 'Roles saved',
  'users.rolesChangedConcurrently': 'Someone else changed this user while the roles were open. Reload to see the current roles; your selection is kept.',
  'users.rolesForbidden': 'You may not change this user’s roles.',
  'users.rolesNotFound': 'This user no longer exists.',
  'users.rolesUnavailable': 'The server is unavailable. Your selection is kept.',
  'users.rolesSaveFailed': 'Could not save the roles.',
  'users.reloadUser': 'Reload user',
  'users.userReloaded': 'Reloaded from the server. Review the roles and save again.',
  'users.reloadFailed': 'Could not reload the user.',
  'users.backToUsers': 'Back to users',
  'users.openCard': 'Open employee card',
  'users.changeRoles': 'Change roles',
  'users.branchesNone': 'No branches assigned.',
  'users.branchesFromWorkplaces': 'A practitioner\u2019s branches follow from their workplaces.',
  'users.ownership': 'Ownership',
  'users.ownershipHint': 'The owner holds full authority over the organization, and there is one owner at a time.',
  'users.card.title': 'Employee card',
  'users.card.notFound': 'This employee is not in the organization\u2019s directory.',
  'users.card.access': 'Access',
  'users.card.manageAccess': 'Change access',

  "roles.title": "Roles and permissions",
  'roles.subtitle': 'Platform-wide role bundles, the capabilities they grant and where they are assigned.',
  'roles.loading': 'Loading roles',
  'roles.loadFailed': 'Could not load roles',
  'roles.forbidden': 'You may not read role definitions',
  'roles.unavailable': 'Role definitions are unavailable right now',
  'roles.capabilitiesLoading': 'Loading the capability catalogue',
  'roles.capabilitiesFailed': 'Could not load the capability catalogue',
  'roles.tableLabel': 'Role definitions',
  'roles.empty': 'No roles',
  'roles.emptyDetail': 'Role definitions returned by the server will appear here.',
  'roles.noMatches': 'No roles match these filters',
  'roles.noMatchesDetail': 'Widen or clear the column filters to return to the full role list.',
  'roles.column.name': 'Name',
  'roles.column.code': 'Code',
  'roles.column.kind': 'Kind',
  'roles.column.lifecycle': 'Lifecycle',
  'roles.column.grants': 'Grants',
  'roles.column.assignments': 'Assignments',
  'roles.column.areas': 'Areas',
  'roles.column.updated': 'Last change',
  'roles.kind.editable': 'Editable role',
  'roles.kind.sealed': 'Sealed role',
  'roles.kind.ownerAuthority': 'Owner authority',
  'roles.lifecycle.active': 'Active',
  'roles.lifecycle.retired': 'Retired',
  'roles.origin.builtIn': 'Built-in',
  'roles.origin.custom': 'Custom',
  'roles.origin.relation': 'Relation-derived',
  'roles.unrecognized': '{value} (unrecognized)',
  'roles.actions': 'Actions',
  'roles.editNamed': 'Edit {name}',
  'roles.openNamed': 'Open {name}',
  'roles.createTitle': 'New role',
  'roles.cloneTitle': 'Clone role',
  'roles.clonedFrom': 'Grants and description copied from {name}. Assignments, origin and history are not copied.',
  'roles.code': 'Role code',
  'roles.codeHint': 'Lowercase letters, digits and underscores. The code cannot change after creation.',
  'roles.displayName': 'Name',
  'roles.description': 'Description',
  'roles.reason': 'Reason for this change',
  'roles.reasonHint': 'Kept in the audit trail together with the exact grant changes.',
  'roles.capabilities': 'Capabilities',
  'roles.capabilitySearch': 'Search capabilities',
  'roles.capabilityNoMatches': 'No capabilities match',
  'roles.keptGrants': 'Grants this catalogue cannot offer, kept as they are',
  'roles.audience.product': 'Product',
  'roles.audience.developer': 'Developer',
  'roles.area.ai': 'AI',
  'roles.area.app': 'Application',
  'roles.area.authorization': 'Authorization',
  'roles.area.clinic': 'Clinic administration',
  'roles.area.clinical': 'Clinical work',
  'roles.area.documents': 'Documents',
  'roles.area.finance': 'Finance',
  'roles.area.imagingStudies': 'Imaging studies',
  'roles.area.laboratory': 'Laboratory',
  'roles.area.messages': 'Messages',
  'roles.area.notifications': 'Notifications',
  'roles.area.patientCases': 'Patient cases',
  'roles.area.patientHospitalStays': 'Hospital stays',
  'roles.area.patientLeads': 'Patient leads',
  'roles.area.patientMessages': 'Patient messages',
  'roles.area.patients': 'Patients',
  'roles.area.practitioners': 'Practitioners',
  'roles.area.printForms': 'Print forms',
  'roles.area.protocols': 'Protocols',
  'roles.area.referenceData': 'Reference data',
  'roles.area.schedule': 'Schedule',
  'roles.area.tasks': 'Tasks',
  'roles.area.tracker': 'Tracker',
  'roles.scopeFor': 'Scope for {name}',
  'roles.scope.none': 'Not granted',
  'roles.scope.own': 'Own',
  'roles.scope.related': 'Related',
  'roles.scope.clinic': 'Clinic',
  'roles.scope.platform': 'Platform',
  'roles.grants': 'Effective grants',
  'roles.grantsNone': 'This entry grants nothing.',
  'roles.facts': 'Definition',
  'roles.fact.origin': 'Origin',
  'roles.fact.lifecycle': 'Lifecycle',
  'roles.fact.assignments': 'Assigned accounts',
  'roles.fact.updated': 'Last change',
  'roles.changes': 'Changes',
  'roles.noChanges': 'No changes yet.',
  'roles.change.added': 'Added',
  'roles.change.removed': 'Removed',
  'roles.change.scopeChanged': 'Scope changed',
  'roles.change.name': 'Name',
  'roles.change.description': 'Description',
  'roles.change.fromTo': '{from} → {to}',
  // What stands where a name or a description was not there. A word
  // rather than a mark, because it sits INSIDE the `{from} to {to}`
  // sentence: an em dash typed into a component is a second absence
  // mark beside `EmptyValue`, and one nobody can translate.
  'roles.change.empty': 'Not set',
  'roles.save': 'Save role',
  'roles.saving': 'Saving',
  'roles.saved': 'Role saved',
  'roles.create': 'Create role',
  'roles.creating': 'Creating',
  'roles.created': 'Role created',
  'roles.clone': 'Clone',
  'roles.retire': 'Retire role',
  'roles.retiring': 'Retiring',
  'roles.retired': 'Role retired',
  'roles.restore': 'Restore role',
  'roles.restoring': 'Restoring',
  'roles.restored': 'Role restored',
  'roles.impactTitle': 'Apply this change to every clinic?',
  'roles.impactDescription': 'Their permissions change as soon as the role is saved. Accounts holding this role across all clinics: {count}.',
  'roles.impactConfirm': 'Save and apply',
  'roles.cancel': 'Cancel',
  'roles.retireTitle': 'Retire this role?',
  'roles.retireDescription': 'A retired role cannot be assigned. Its definition and history are kept, and it can be restored later.',
  'roles.retireConfirm': 'Retire',
  'roles.restoreTitle': 'Restore this role?',
  'roles.restoreDescription': 'The role becomes assignable again under a new revision.',
  'roles.restoreConfirm': 'Restore',
  'roles.readOnly': 'Read-only',
  'roles.sealedDefault': 'This role is sealed by the platform release and cannot be edited here.',
  'roles.ownerAuthorityNote': 'Owner authority follows the clinic owner relation. It is not a role: it cannot be assigned and cannot be edited here.',
  'roles.retiredNote': 'This role is retired and cannot be assigned. Restore it before editing it.',
  'roles.validation.code': 'Enter a role code',
  'roles.validation.name': 'Enter a name',
  'roles.validation.grants': 'Grant at least one capability',
  'roles.validation.reason': 'Enter a reason',
  'roles.error.notFound': 'This role no longer exists on the server.',
  'roles.error.forbidden': 'You may not change this role.',
  'roles.error.scopeInvalid': 'The server refused one of the grant scopes. Review the scopes and save again.',
  'roles.error.changedConcurrently': 'Someone else changed this role while the editor was open. Reload it to see the current revision; your unsaved edits are kept.',
  'roles.error.inUse': 'This role is still assigned and cannot be retired.',
  'roles.error.unavailable': 'The server is unavailable. Your unsaved edits are kept.',
  'roles.error.invalid': 'The server rejected this role definition.',
  'roles.error.unknown': 'The server returned an unexpected answer. Your unsaved edits are kept.',
  'roles.reload': 'Reload role',
  'roles.reloaded': 'Reloaded from the server. Review the changes and save again.',
  'roles.reloadFailed': 'Could not reload the role.',
  'roles.unsavedIntent': 'Role definition \u2014 entries that were never saved',
  'roles.detailLoading': 'Loading role',
  'roles.detailFailed': 'Could not load this role',
} as const
